import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowRight,
  FaEye,
  FaEyeSlash,
  FaExclamationCircle,
  FaLock,
  FaShieldAlt,
  FaUser,
} from "react-icons/fa";
import { useAuth } from "../hooks/useAuth";
import Footer from "../components/CommonComponents/Footer";
import SubscriptionExpiredModal from "../components/CommonComponents/SubscriptionExpiredModal";
import "../index.css";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [mfaCode, setMfaCode] = useState(Array(6).fill(""));
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [otpShake, setOtpShake] = useState(false);
  const [expiredSubscription, setExpiredSubscription] = useState(null);
  const otpInputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyEmailMfa } = useAuth();

  let role = "pos";
  if (location.pathname.includes("/superadmin")) role = "superadmin";
  else if (location.pathname.includes("/admin")) role = "admin";

  const loginConfig = {
    superadmin: {
      badge: "System administration",
      title: "Super Admin Portal",
      subtitle: "Sign in to manage customer accounts, access control, and system-wide settings.",
      heroTitle: "Super Admin Access",
      heroDescription:
        "Securely manage platform-wide configuration, customer setup, and overall account control.",
      featureOneLabel: "Customers",
      featureOneText:
        "Review customer records, account details, and service access from one dashboard.",
      featureTwoLabel: "Control",
      featureTwoText:
        "Handle permissions, system visibility, and key administrative actions with confidence.",
    },
    admin: {
      badge: "Store management",
      title: "Admin Control Center",
      subtitle: "Sign in to oversee reports, inventory, users, and daily store operations.",
      heroTitle: "Admin Management Portal",
      heroDescription:
        "Monitor business activity, manage inventory, and keep daily operations running smoothly.",
      featureOneLabel: "Reports",
      featureOneText:
        "Track sales performance, daily summaries, and operational records in one place.",
      featureTwoLabel: "Inventory",
      featureTwoText:
        "Manage stock, product information, and branch activity with better visibility.",
    },
    pos: {
      badge: "Point of sale access",
      title: "POS Staff Login",
      subtitle: "Open your workspace and continue serving customers smoothly.",
      heroTitle: "Point of Sale System",
      heroDescription:
        "Sign in to manage sales, process transactions, and continue daily store operations.",
      featureOneLabel: "Sales",
      featureOneText:
        "Access the cashier dashboard and handle customer transactions quickly.",
      featureTwoLabel: "Operations",
      featureTwoText:
        "View products, monitor records, and continue branch activity from one place.",
    },
  };

  const activeConfig = loginConfig[role];

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;

    if (!username.trim() || !password.trim()) {
      setLoginError("Please enter both username and password.");
      return;
    }

    setLoginError("");
    setIsLoggingIn(true);
    const result = await login({ username: username.trim(), password }, role);
    setIsLoggingIn(false);

    if (result.mfaRequired) {
      setMfaChallenge({
        challengeId: result.challengeId,
        maskedEmail: result.maskedEmail,
        expiresInSeconds: result.expiresInSeconds,
      });
      setPassword("");
      setMfaCode(Array(6).fill(""));
      return;
    }

    if (result.success) {
      if (role === "superadmin") navigate("/superadmin/dashboard");
      else if (role === "admin") navigate("/admin/dashboard");
      else navigate("/dashboard");
      return;
    }

    if (result.code === "SUBSCRIPTION_EXPIRED") {
      setExpiredSubscription(result.details || {});
      setPassword("");
      return;
    }

    setLoginError(result.error || "Login failed.");
  };

  useEffect(() => {
    if (!mfaChallenge) return;
    const timerId = window.setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    return () => window.clearTimeout(timerId);
  }, [mfaChallenge]);

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    const code = mfaCode.join("").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setLoginError("Enter the 6-digit verification code.");
      setOtpShake(true);
      return;
    }

    setLoginError("");
    setIsVerifyingMfa(true);
    const result = await verifyEmailMfa({ challengeId: mfaChallenge?.challengeId, code }, role);
    setIsVerifyingMfa(false);

    if (result.success) {
      if (role === "superadmin") navigate("/superadmin/dashboard");
      else if (role === "admin") navigate("/admin/dashboard");
      else navigate("/dashboard");
      return;
    }

    setLoginError(result.error || "Verification failed.");
    setOtpShake(true);
  };

  const handleBackToPassword = () => {
    setMfaChallenge(null);
    setMfaCode(Array(6).fill(""));
    setLoginError("");
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextCode = [...mfaCode];
    nextCode[index] = digit;
    setMfaCode(nextCode);
    if (loginError) setLoginError("");
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key !== "Backspace") return;
    const digit = mfaCode[index] || "";
    if (!digit && index > 0) {
      event.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    const pastedCode = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pastedCode) return;
    event.preventDefault();
    const nextCode = Array(6).fill("");
    pastedCode.split("").forEach((digit, index) => {
      nextCode[index] = digit;
    });
    setMfaCode(nextCode);
    if (loginError) setLoginError("");
    otpInputRefs.current[Math.min(pastedCode.length, 6) - 1]?.focus();
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#06142f]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(232,180,94,0.16),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(90,120,255,0.2),_transparent_28%),linear-gradient(135deg,_#081A4B_0%,_#06142f_45%,_#020817_100%)]" />
        <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-amber-200/10 blur-3xl" />
        <div className="absolute right-0 top-1/3 h-96 w-96 rounded-full bg-blue-400/10 blur-3xl" />
      </div>

      <div className="relative flex flex-1 items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid w-full max-w-5xl overflow-hidden rounded-[28px] border border-white/10 bg-white/8 shadow-[0_32px_80px_rgba(2,8,23,0.45)] backdrop-blur-xl lg:grid-cols-[1.1fr_0.9fr]">
          <div className="hidden flex-col justify-between border-b border-white/10 px-6 py-8 sm:px-10 lg:flex lg:border-b-0 lg:border-r">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-sm text-slate-950">
                  <FaShieldAlt />
                </span>
                {activeConfig.badge}
              </div>

              <div className="mt-8 space-y-4">
                <h1 className="max-w-md text-4xl font-semibold leading-tight text-white sm:text-5xl">
                  {activeConfig.heroTitle}
                </h1>
                <p className="max-w-lg text-sm leading-7 text-slate-300 sm:text-base">
                  {activeConfig.heroDescription}
                </p>
              </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  {activeConfig.featureOneLabel}
                </p>
                <p className="mt-2 text-sm font-medium text-white">
                  {activeConfig.featureOneText}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  {activeConfig.featureTwoLabel}
                </p>
                <p className="mt-2 text-sm font-medium text-white">
                  {activeConfig.featureTwoText}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white px-6 py-8 sm:px-10 lg:px-12 lg:py-10">
            <div className="mx-auto flex h-full w-full max-w-md flex-col justify-center">
              <div className="mb-8">
                <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-700 lg:hidden">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-sm text-slate-950">
                    <FaShieldAlt />
                  </span>
                  {activeConfig.badge}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">
                      ZeroOne
                    </p>
                    <h2 className="mt-2 text-[1.65rem] font-semibold leading-tight tracking-tight text-slate-900 sm:mt-3 sm:text-3xl">
                      {activeConfig.title}
                    </h2>
                  </div>
                  <div
                    aria-hidden="true"
                    className="pointer-events-none mt-1 inline-flex shrink-0 select-none items-center gap-2 rounded-full border border-slate-900/15 bg-slate-900/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white shadow-sm sm:text-[11px] sm:tracking-[0.1em]"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                    Sign in
                  </div>
                </div>
                <p className="mt-3 max-w-sm text-[13px] leading-5 text-slate-500 sm:mt-4 sm:text-sm sm:leading-6">
                  {activeConfig.subtitle}
                </p>
              </div>

              {mfaChallenge ? (
                <form onSubmit={handleVerifyMfa} className="space-y-5" noValidate>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">
                      Verification Code
                    </span>
                    <div className="flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 shadow-sm transition focus-within:border-slate-900 focus-within:bg-white focus-within:ring-4 focus-within:ring-slate-200/70 sm:gap-3 sm:px-5">
                      <FaShieldAlt className="hidden shrink-0 text-slate-400 sm:block" />
                      <div
                        className={`grid min-w-0 flex-1 grid-cols-6 gap-2 sm:gap-3 ${otpShake ? "otp-code-shake" : ""}`}
                        onAnimationEnd={() => setOtpShake(false)}
                      >
                        {Array.from({ length: 6 }).map((_, index) => (
                          <input
                            key={index}
                            ref={(element) => {
                              otpInputRefs.current[index] = element;
                            }}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={mfaCode[index] || ""}
                            onChange={(event) => handleOtpChange(index, event.target.value)}
                            onKeyDown={(event) => handleOtpKeyDown(index, event)}
                            onPaste={handleOtpPaste}
                            className="h-12 w-full appearance-none rounded-none border-0 border-b-2 border-slate-300 bg-transparent p-0 text-center text-2xl font-semibold text-slate-900 caret-slate-900 outline-none shadow-none transition focus:border-blue-600 focus:outline-none focus:ring-0"
                            aria-label={`Verification code digit ${index + 1}`}
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      We sent a code to {mfaChallenge.maskedEmail || "your superadmin email"}. It expires in 10 minutes.
                    </p>
                  </label>

                  <button
                    type="submit"
                    disabled={isVerifyingMfa}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70 focus:outline-none focus:ring-4 focus:ring-slate-300"
                  >
                    {isVerifyingMfa ? "Verifying..." : "Verify and continue"}
                    <FaArrowRight className="text-xs" />
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToPassword}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Back to password
                  </button>

                  {loginError ? (
                    <div className="rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-rose-50 px-4 py-3 shadow-sm shadow-rose-100/70">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-sm text-rose-600">
                          <FaExclamationCircle />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-rose-700">
                            Verification failed
                          </p>
                          <p className="mt-1 text-sm leading-5 text-rose-600">
                            {loginError}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </form>
              ) : (
              <form onSubmit={handleLogin} className="space-y-5" noValidate>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Username or Email
                  </span>
                  <div className="group relative">
                    <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition group-focus-within:text-slate-900" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (loginError) setLoginError("");
                      }}
                      required
                      placeholder="Enter your username or email"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-4 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-200/70"
                      autoComplete="username"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Password
                  </span>
                  <div className="group relative">
                    <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition group-focus-within:text-slate-900" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (loginError) setLoginError("");
                      }}
                      required
                      placeholder="Enter your password"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-12 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-200/70"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-900 focus:outline-none"
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>

                <div className="flex items-center justify-between gap-4 text-xs text-slate-500">
                  <p>Use your assigned credentials to continue.</p>
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                    Secure
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70 focus:outline-none focus:ring-4 focus:ring-slate-300"
                >
                  {isLoggingIn ? "Signing in..." : "Continue to workspace"}
                  <FaArrowRight className="text-xs" />
                </button>

                {loginError ? (
                  <div className="rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-rose-50 px-4 py-3 shadow-sm shadow-rose-100/70">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-sm text-rose-600">
                        <FaExclamationCircle />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-rose-700">
                          Sign in failed
                        </p>
                        <p className="mt-1 text-sm leading-5 text-rose-600">
                          {loginError}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer
        className="relative py-4 text-xs"
        variant="light"
        noBorder
        noPadding
        showLegalLinks
        companyHref="https://zerooneitinc.com/"
      />
      <SubscriptionExpiredModal
        isOpen={Boolean(expiredSubscription)}
        details={expiredSubscription}
        onClose={() => setExpiredSubscription(null)}
      />
    </div>
  );
}
